<template>
  <transition :duration="1000" v-on:before-leave="beforeLeave">
    <div class="local" v-bind:class="activeCategory">
      <div id="local-map" class="local-map__container"></div>
      <local-nav
        v-if="!loading"
        v-bind:config="navItems.sortedList()"
        v-bind:opened="navOpened"
        v-bind:toggle-active="toggleActive"
        v-bind:description="navDescription"
        v-bind:is-animating="isAnimating"
        v-bind:active-parent="activeParent"
      >
      </local-nav>
    </div>
  </transition>
</template>
<script>
import { isUndefined } from 'lodash';
import LocalNav from '@/components/LocalNav';
import SurreyMap from '@/services/Map';
import config from '@/services/Config';

export default {
  name: 'local',
  components: { LocalNav },
  data() {
    return {
      map: new SurreyMap({ containerId: 'local-map' }),
      navItems: null,
      loading: true,
      activeCategory: null,
      navOpened: false,
      toggleActive: false,
      destroyed: false,
      navDescription: null,
      isAnimating: false,
      activeParent: null,
    };
  },
  methods: {
    beforeLeave() {
      this.destroyed = true;
    },
    openDestination(link, layerSlug) {
      this.isAnimating = true;
      return this.map.setDestination(link, layerSlug).then(() => {
        this.isAnimating = false;
      });
    },
    openCategory(link) {
      this.isAnimating = true;
      return this.map.setCategory(link).then(() => {
        this.isAnimating = false;
      });
    },
    async fetchData() {
      const data = await config.get();
      this.navItems = data.local.nav;
      this.navDescription = data.metaData.localDescription;
      this.loading = false;
      await this.map.initMap(data.metaData, this.navItems);
      let action = null;
      this.activeParent = this.navItems.find(this.$route.params.parentId);
      if (isUndefined(this.$route.params.parentId) === false) {
        const parent = this.navItems.find(this.$route.params.parentId);
        if (isUndefined(this.$route.params.id) === false) {
          action = this.openDestination(
            parent.children().find(this.$route.params.id),
          );
        } else {
          action = this.openCategory(parent);
        }
      } else {
        this.isAnimating = true;
        action = this.map.flyTo([-122.801094, 49.10443], 'holistic');
      }

      await action;
      if (!window.matchMedia('(max-width: 600px)').matches) {
        this.navOpened = true;
      }
      this.toggleActive = true;
      this.isAnimating = false;
      this.map.toggleAllBoundaryLayers('visible');
      this.map.updateMarkers();
    },
  },
  mounted() {
    this.fetchData();
  },
  destroyed() {
    this.map.destroy();
  },
  beforeRouteUpdate(to, from, next) {
    this.activeParent = this.navItems.find(to.params.parentId);
    if (!to.params.parentId) {
      this.map.closePopups();
      this.map.toggleMarkerLayerVisibility();
      this.map.toggleRouteLayerVisibility();
      this.map.removeMarkers();
      this.map.toggleAllBoundaryLayers('visible');
      this.map.flyTo([-122.801094, 49.10443], 'holistic');
    }

    if (
      isUndefined(to.params.parentId) === false &&
      isUndefined(to.params.id)
    ) {
      this.openCategory(this.navItems.find(to.params.parentId));
    }
    this.activeCategory = to.params.parentId;
    if (
      isUndefined(to.params.id) === false &&
      isUndefined(to.params.grandchild) &&
      to.params.id !== from.params.id
    ) {
      this.openDestination(
        this.navItems.find(to.params.parentId).children().find(to.params.id),
      );
    }

    if (
      isUndefined(to.params.grandchild) === false &&
      to.params.grandchild !== from.params.grandchild
    ) {
      const item = this.navItems
        .find(to.params.parentId)
        .children()
        .find(to.params.id)
        .children()
        .find(to.params.grandchild);
      this.openDestination(item);
    }

    if (window.matchMedia('(max-width: 600px)').matches) {
      this.navOpened = false;
    }
    next();
  },
};
</script>
<style lang="scss">
@import "../main.scss";

.local,
.local-map__container {
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  transition: opacity 0.5s $standardEasing;
  opacity: 1;
}

.v-leave-active {
  .local,
  .local-map__container {
    opacity: 0;
  }
}

.marker {
  font-size: 1.4rem;
  font-family: $defaultFont;

  width: 2.4em;
  height: 2.4em;
  color: white;
  text-align: center;
}

.marker:before {
  background: url("/static/marker-local-group.svg") no-repeat center center;
  background-size: contain;
  content: "";
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.marker--cluster {
  width: 3.2em;
  height: 3.2em;

  .marker__count {
    z-index: 3;
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.marker--unclustered {
  .marker__icon {
    width: 1.8rem;
    height: 1.8rem;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: contain;
    z-index: 3;
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: -0.9rem;
    margin-top: -0.9rem;
    overflow: hidden;

    &:hover, &:active  {
      ~ .marker__label {
        opacity: 1;
        pointer-events: unset;
      }
    }
  }

  .marker__label {
    display: flex;
    flex-direction: column;
    justify-content: center;
    opacity: 0;
    pointer-events: none;
    align-items: center;
    background: white;
    color: $appPrimary;
    font-size: 1rem;
    position: absolute;
    height: 2em;
    top: 0;
    left: 50%;
    padding-left: calc(1em + 0.9rem);
    height: 100%;
    z-index: 0;
    transition: opacity 200ms ease-in-out;
    line-height: 1.06rem;
    text-align: left;
    width: max-content;
    padding-right: 1rem;
  }

  .marker__label p {
    margin: 0;
    padding: 0;
    width: 100%;
  }

  &.marker--active .marker__label {
    opacity: 1;
    pointer-events: unset;
  }
}

@each $name, $value in $customIcons {
  .marker__icon--#{$name} {
    background-image: #{"url(../assets/icons/#{$value})"};
  }
}

.mapboxgl-ctrl.mapboxgl-ctrl-attrib {
  padding: 0 2px !important;
  font-size: 0.9rem;
  background: none;
}

.mapboxgl-popup-content {
  background: rgba(#092531, 0.75);
  max-width: 400px;
  min-height: 50px;
  max-height: 700px;
  padding: 0;
  font-family: $defaultFont;
  border-radius: 10px !important;
  color: #fff;

  > .image--large {
    width: 400px;
  }

  > iframe,
  > video {
    width: 400px;
    height: 225px;
  }

  > h1 {
    padding: 0 10px;
    font-size: 3.6rem;
    line-height: 100%;
    color: #fff;
    font-weight: 200;
    letter-spacing: 4px;

    &.title--events {
      font-size: 3.2rem;
      line-height: 3.2rem;
    }
  }

  > h2 {
    padding: 0 10px;
    font-size: 1.2rem;
    font-weight: 700;
    letter-spacing: 4px;
    &.subtitle--events {
      font-size: 1.8rem;
      line-height: 1.8rem;
      font-weight: 300;
    }
  }

  > p {
    font-size: 1.2rem;
    font-weight: 200;
    text-transform: none;
    padding: 0 10px;

    &.paragraph--events {
      font-size: 1.4rem;
      line-height: 1.4rem;
      margin: 0 0 0.5rem 0;
      font-weight: 300;
    }
  }

  .popup__gallery {
    padding: 0 10px;
    img {
      width: 30%;
      margin: 0 3% 0 0;
      cursor: pointer;
      &:last-child {
        margin-right: 0;
      }
    }
  }
}

.mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip {
  display: none;
}

.mapboxgl-popup-tip {
  display: none;
}

.mapboxgl-popup-anchor-left .mapboxgl-popup-tip {
  &:before {
    left: auto;
    right: -70px;
  }
}

.mapboxgl-popup {
  opacity: 1;
  max-width: unset !important;

  // animation: fadeIn 0.5s $standardEasing;
}

.mapboxgl-popup-close-button {
  width: 25px;
  height: 25px;
  color: #fff;
  line-height: 0;
  padding: 0;
  text-indent: 100%;
  white-space: nowrap;
  overflow: hidden;
  top: 0.5rem;
  right: 0.5rem;
  &:before {
    content: "";
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
    background: url(../assets/icon-close-light.svg) no-repeat center center;
    background-size: 15px;
  }
}

@media only screen and (max-width: 600px) {
  .local .modal img {
    width: 100%;
  }

  .mapboxgl-popup-close-button:before {
    background: url(../assets/icon-close.svg) no-repeat center center;
    background-size: 15px;
  }
  .mapboxgl-popup {
    position: fixed;
    bottom: 0;
    top: auto;
    left: 0;
    right: 0;
    max-width: unset;
    width: 100%;
    height: 80%;
    max-height: unset;
    transform: none !important;
  }

  .mapboxgl-popup-content {
    max-width: unset;
    max-height: unset;
    min-width: unset;
    min-height: unset;
    background: #f6f6f6;
    color: $appBody;
    display: flex;
    flex-direction: column;
    height: 100%;

    > h1 {
      font-size: 1.6rem;
      color: $appBody;
    }

    > .image--large {
      width: unset;
      max-width: 80%;
      height: 20vh;
      align-self: center;
    }

    > .popup__gallery {
      margin-top: auto;
      margin-bottom: 2rem;
    }

    
  }
}

.mapboxgl-ctrl.mapboxgl-ctrl-attrib a {
  color: rgba(255, 255, 255, 0.25);
}

.mapboxgl-ctrl.mapboxgl-ctrl-attrib a:hover {
  color: rgba(255, 255, 255, 0.5);
}

.mapboxgl-ctrl-logo {
  opacity: 0.5;
}
</style>



// WEBPACK FOOTER //
// src/components/Local.vue